import { ChangeEvent, useCallback, useEffect, useState } from 'react';

import { filterXSS } from 'xss';
import { toast } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import { useRoomStore } from '@src/stores';

import { generateRandomRoomId } from '@src/helpers';
import { generateRandomUUID } from '@src/utils';

const useCreateRoom = () => {
  const lastRoom = window.localStorage.lastRoom ?? '';
  const [lastRoomLink, setLastRoomLink] = useState('');
  const [meetLink, setMeetLink] = useState('');

  const { roomId, setRoomId, roomTitle, setRoomTitle } = useRoomStore(
    useShallow((state) => ({
      roomId: state.roomId,
      roomTitle: state.roomTitle,
      setRoomId: state.setRoomId,
      setRoomTitle: state.setRoomTitle,
    }))
  );

  useEffect(() => {
    setLastRoomLink(`/join/?room=${lastRoom}`);
  }, [lastRoom]);

  useEffect(() => {
    setMeetLink(`${window.location.origin}/join/?room=${roomId}`);
  }, [roomId, roomTitle]);

  useEffect(() => {
    generateRandomRoomId(setRoomId);
  }, [setRoomId]);

  const generateRoom = useCallback(() => {
    setRoomId(generateRandomUUID());
  }, [setRoomId]);

  const isValidRoomId = (input: string) => {
    if (typeof input !== 'string') {
      return false;
    }
    const pathTraversalPattern = /(\.\.(\/|\\))+/;
    return !pathTraversalPattern.test(input);
  };

  const joinRoom = useCallback(() => {
    const roomValid = isValidRoomId(roomId);

    if (!roomId) {
      alert('Room name empty!\nPlease pick a room name.');
      return;
    }
    if (!roomValid) {
      alert('Invalid Room name!\nPath traversal pattern detected!');
      return;
    }

    setRoomId(roomId);
    window.open(`${meetLink}${roomTitle ? '&roomTitle=' + roomTitle : ''}`, '_blank');
    window.localStorage.lastRoom = roomId;
    setRoomTitle('');
    generateRandomRoomId(setRoomId);
  }, [meetLink, roomId, roomTitle, setRoomId, setRoomTitle]);

  const onChangeRoomId = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const name = filterXSS(e.target.value).trim().replace(/\s+/g, '-');
      setRoomId(name);
    },
    [setRoomId]
  );

  const onChangeRoomTitle = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setRoomTitle(e.target.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCopyRoomId = useCallback(() => {
    navigator.clipboard.writeText(meetLink);
    toast.success('Link copied successfully', {
      position: 'top-left',
      className: 'toast toast-black',
      autoClose: 1000,
    });
  }, [meetLink]);

  return {
    onChangeRoomId,
    generateRoom,
    joinRoom,
    meetLink,
    lastRoomLink,
    lastRoom,
    onCopyRoomId,
    roomTitle,
    onChangeRoomTitle,
  };
};

export default useCreateRoom;
